import './card.css'
import React, { useEffect, useMemo, useRef, useState } from "react";
import Axios from "axios";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import CoverDisplay from '../components/ImagesPage.js';
import Icons from '../dashboard/components/pokemon.js'
import { MutatingDots } from 'react-loader-spinner'

const CARD_SEARCH_URL = "https://api.alientrader.cards/data/dbsearch";
const INITIAL_RENDER_COUNT = 120;
const RENDER_BATCH_SIZE = 120;

function ImagesV2() {
  const [cardList, setCardList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [visibleCount, setVisibleCount] = useState(INITIAL_RENDER_COUNT);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      setVisibleCount(INITIAL_RENDER_COUNT);

      try {
        const res = await Axios.get(CARD_SEARCH_URL);
        if (isMounted) {
          const sortedCards = Array.isArray(res.data)
            ? [...res.data].sort((a, b) => Number(b.card_id) - Number(a.card_id))
            : [];

          setCardList(sortedCards);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (isMounted) {
          setError("Unable to load card covers right now.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const visibleCards = useMemo(
    () => cardList.slice(0, visibleCount),
    [cardList, visibleCount]
  );

  useEffect(() => {
    if (loading || error || visibleCount >= cardList.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];

        if (firstEntry?.isIntersecting) {
          setVisibleCount((currentCount) =>
            Math.min(currentCount + RENDER_BATCH_SIZE, cardList.length)
          );
        }
      },
      {
        rootMargin: "600px 0px",
      }
    );

    const currentTarget = loadMoreRef.current;

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
      observer.disconnect();
    };
  }, [cardList.length, error, loading, visibleCount]);

  return (
    <div className="imagesPage">
      {loading ? (
        <div className="loader-container">
          <MutatingDots
            visible={true}
            height="100"
            width="100"
            color="#4fa94d"
            secondaryColor="#4fa94d"
            radius="12.5"
            ariaLabel="mutating-dots-loading"
            wrapperStyle={{}}
            wrapperClass=""
          />
        </div>
      ) : error ? (
        <Container>
          <Row className='d-flex justify-content-center'>
            <Icons />
          </Row>
          <Row><br /></Row>
          <Row className='justify-content-center'>
            <Col xs="auto">
              <div className='custom-info-header'>{error}</div>
            </Col>
          </Row>
        </Container>
      ) : (
        <Container>
          <Row className='d-flex justify-content-center'>
            <Icons />
          </Row>
          <Row><br /></Row>
          <Row className='justify-content-center'>
            <Col xs="auto">
              <div className='custom-info-header'>
                Showing {visibleCards.length.toLocaleString()} of {cardList.length.toLocaleString()} cards
              </div>
            </Col>
          </Row>
          <Row><br /></Row>
          <Row>
            {visibleCards.map((item, index) => (
              <Col className='imagesPageRow d-flex align-items-center justify-content-center' key={item.card_id}>
                <CoverDisplay key={index} image={item} />
              </Col>
            ))}
          </Row>
          {visibleCount < cardList.length ? (
            <>
              <Row><br /></Row>
              <Row className='justify-content-center'>
                <Col xs="auto">
                  <div className='custom-info-header'>Loading more cards as you scroll...</div>
                </Col>
              </Row>
              <div ref={loadMoreRef} style={{ height: "1px" }} />
            </>
          ) : null}
        </Container>
      )}
      <br />
    </div>
  );
}

export default ImagesV2;
